import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { api, EmbedWidget } from './api';
import { Icon } from './icons';

const MUTED = 'var(--fx-muted,#9092a0)';
const ACCENT = 'var(--fx-accent,#f7b500)';

/** Admin modal: create + manage embeddable chat widgets for a workspace. Each widget
 *  is a public <script> snippet an external site drops in; visitors chat into a
 *  private channel that owners/admins auto-join. Opened from the More menu. */
export function EmbedWidgetsModal({ wid, onClose }: { wid: string; onClose: () => void }) {
  const [items, setItems] = useState<EmbedWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('Website chat');
  const [greeting, setGreeting] = useState('Hi! How can we help?');
  const [accent, setAccent] = useState('#f7b500');
  const [requireEmail, setRequireEmail] = useState(false);
  const [origins, setOrigins] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.listWidgets(wid).then((r) => { if (alive) setItems(r); }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [wid]);

  const create = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const w = await api.createWidget(wid, {
        name: name.trim() || 'Website chat',
        greeting: greeting.trim() || undefined,
        accent: accent.trim() || undefined,
        require_email: requireEmail,
        allowed_origins: origins.trim() || undefined,
      });
      setItems((c) => [w, ...c]);
      setName('Website chat'); setGreeting('Hi! How can we help?'); setAccent('#f7b500'); setRequireEmail(false); setOrigins('');
    } catch (e: any) {
      window.alert(e?.message || 'Could not create the widget.');
    } finally { setBusy(false); }
  };

  const toggle = async (w: EmbedWidget) => {
    try {
      const u = await api.updateWidget(w.id, { enabled: !w.enabled });
      setItems((c) => c.map((x) => (x.id === w.id ? u : x)));
    } catch { /* ignore */ }
  };

  const editOrigins = async (w: EmbedWidget) => {
    const next = window.prompt(
      'Restrict this widget to these websites (comma-separated domains, e.g. acme.com, shop.acme.com).\nLeave blank to allow any site.',
      w.allowed_origins || '',
    );
    if (next === null) return;
    try {
      const u = await api.updateWidget(w.id, { allowed_origins: next.trim() });
      setItems((c) => c.map((x) => (x.id === w.id ? u : x)));
    } catch (e: any) { window.alert(e?.message || 'Could not update allowed sites.'); }
  };

  const remove = async (w: EmbedWidget) => {
    if (!window.confirm(`Delete “${w.name}”? The snippet stops working and its web conversations are detached.`)) return;
    try { await api.deleteWidget(w.id); setItems((c) => c.filter((x) => x.id !== w.id)); } catch { /* ignore */ }
  };

  const copy = (w: EmbedWidget) => {
    try {
      navigator.clipboard.writeText(w.snippet);
      setCopied(w.id);
      window.setTimeout(() => setCopied((c) => (c === w.id ? null : c)), 1600);
    } catch { /* ignore */ }
  };

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fx-dlg-overlay" />
        <Dialog.Content className="fx-dlg" aria-describedby={undefined}>
          <div className="fx-dlg-head">
            <span className="fx-dlg-ic"><Icon name="link" size={18} /></span>
            <Dialog.Title className="fx-dlg-title">Website chat widget</Dialog.Title>
            <Dialog.Close className="fx-dlg-x" aria-label="Close"><Icon name="close" size={18} /></Dialog.Close>
          </div>

          <div className="fx-dlg-body">
            <div className="fx-dlg-value" style={{ color: MUTED, marginBottom: 14 }}>
              Embed a live chat bubble on any website. Visitors' messages arrive as a private
              conversation your owners &amp; admins can answer right here.
            </div>

            {/* Existing widgets */}
            {loading ? (
              <div className="fx-dlg-value">Loading…</div>
            ) : items.length === 0 ? (
              <div className="fx-dlg-value" style={{ color: MUTED }}>No widgets yet — create one below.</div>
            ) : (
              items.map((w) => (
                <div key={w.id} style={{ border: '1px solid rgba(128,128,128,0.18)', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 12, height: 12, borderRadius: '50%', background: w.accent, flex: 'none', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' }} />
                    <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{w.name}</span>
                    <span style={{ fontSize: 11, color: w.enabled ? ACCENT : MUTED }}>{w.enabled ? 'Live' : 'Off'}</span>
                    <button type="button" onClick={() => editOrigins(w)} style={linkBtn()}>Allowed sites</button>
                    <button type="button" onClick={() => toggle(w)} style={linkBtn()}>{w.enabled ? 'Disable' : 'Enable'}</button>
                    <button type="button" onClick={() => remove(w)} style={{ ...linkBtn(), color: '#e56a6a' }}>Delete</button>
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'stretch' }}>
                    <code style={{
                      flex: 1, fontSize: 11, lineHeight: 1.4, background: 'rgba(128,128,128,0.12)', borderRadius: 8,
                      padding: '8px 10px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'ui-monospace,Menlo,Consolas,monospace',
                    }}>{w.snippet}</code>
                    <button type="button" className="fx-btn" style={{ flex: 'none' }} onClick={() => copy(w)}>
                      {copied === w.id ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  {w.require_email && <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>Asks visitors for their email before chatting.</div>}
                  <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>
                    {w.allowed_origins
                      ? <>Only loads on: <span style={{ color: 'var(--fx-text,#e8e8ee)' }}>{w.allowed_origins}</span></>
                      : <span style={{ color: '#d99a4e' }}>⚠ Loads on any website — set Allowed sites to lock it down.</span>}
                  </div>
                </div>
              ))
            )}

            {/* Create */}
            <div style={{ borderTop: '1px solid rgba(128,128,128,0.18)', marginTop: 12, paddingTop: 14 }}>
              <div className="fx-dlg-label">New widget</div>
              <input className="fx-text-input" style={fieldCss} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (internal)" />
              <input className="fx-text-input" style={fieldCss} value={greeting} onChange={(e) => setGreeting(e.target.value)} placeholder="Greeting shown to visitors" />
              <input className="fx-text-input" style={fieldCss} value={origins} onChange={(e) => setOrigins(e.target.value)} placeholder="Allowed sites (e.g. acme.com, shop.acme.com) — blank = any" />
              <div style={{ fontSize: 11, color: MUTED, margin: '-4px 0 8px' }}>Recommended: list the domains you'll embed this on so nobody else can reuse your snippet.</div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '4px 0 10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <span style={{ color: MUTED }}>Accent</span>
                  <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} style={{ width: 30, height: 26, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={requireEmail} onChange={(e) => setRequireEmail(e.target.checked)} />
                  Require email first
                </label>
              </div>
              <button type="button" className="fx-sendbtn" disabled={busy} onClick={create}>
                {busy ? 'Creating…' : 'Create widget'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const fieldCss: React.CSSProperties = { width: '100%', boxSizing: 'border-box', marginBottom: 8 };
function linkBtn(): React.CSSProperties {
  return { background: 'none', border: 'none', color: 'var(--fx-accent,#f7b500)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', padding: '2px 4px' };
}
